/**
 * Composant overlay pour le tutoriel interactif
 * Met en surbrillance le champ actif avec un spotlight
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { X, ChevronRight, Lightbulb, Save } from 'lucide-react';
import type { TutorialStep } from '../constants/tutorialSteps';

// Feature flag : Activer/désactiver l'auto-progression
const ENABLE_AUTO_PROGRESSION = false;

interface TutorialSpotlightProps {
  currentStep: TutorialStep;
  stepNumber: number;
  totalSteps: number;
  isFieldValid: boolean;
  onNext: () => void;
  onPrevious: () => void;
  onSkip: () => void;
  placement?: 'auto' | 'top';
  showNavigation?: boolean;
  onSaveDraft?: () => void;
  isSavingDraft?: boolean;
  saveLabel?: string;
}

export const TutorialSpotlight = ({
  currentStep,
  stepNumber,
  totalSteps,
  isFieldValid,
  onNext,
  onPrevious,
  onSkip,
  placement = 'auto',
  showNavigation = true,
  onSaveDraft,
  isSavingDraft = false,
  saveLabel = 'Sauvegarder',
}: TutorialSpotlightProps) => {
  const [targetRects, setTargetRects] = useState<DOMRect[]>([]);
  const [isUserInteracting, setIsUserInteracting] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout>();
  const inactivityTimeoutRef = useRef<NodeJS.Timeout>();
  const hasScrolledRef = useRef<boolean>(false);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [tooltipHeight, setTooltipHeight] = useState(200);

  useEffect(() => {
    if (!tooltipRef.current) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const height = entry.contentRect.height;
        if (height > 0) {
          setTooltipHeight(height);
        }
      }
    });
    observer.observe(tooltipRef.current);
    return () => observer.disconnect();
  }, [currentStep]);

  const fieldRefs = useMemo(() => {
    const ref = currentStep.fieldRef;
    return Array.isArray(ref) ? ref : [ref];
  }, [currentStep.fieldRef]);

  useEffect(() => {
    const updateTargetPosition = (shouldScroll = false) => {
      const rects: DOMRect[] = [];
      fieldRefs.forEach((fieldRef) => {
        const element = document.getElementById(fieldRef);
        if (element) {
          const rect = element.getBoundingClientRect();
          rects.push(rect);
        }
      });

      if (rects.length > 0) {
        setTargetRects(rects);

        if (shouldScroll && !hasScrolledRef.current && rects.length > 0) {
          hasScrolledRef.current = true;
          const firstElement = document.getElementById(fieldRefs[0]);
          if (firstElement) {
            if (placement === 'top') {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            } else {
              firstElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }
        }
      }
    };

    const handleFocus = () => {
      setIsUserInteracting(true);
      if (inactivityTimeoutRef.current) clearTimeout(inactivityTimeoutRef.current);
    };

    const handleBlur = () => {
      setIsUserInteracting(false);
      if (inactivityTimeoutRef.current) clearTimeout(inactivityTimeoutRef.current);
    };

    const handleInput = () => {
      setIsUserInteracting(true);
      if (inactivityTimeoutRef.current) clearTimeout(inactivityTimeoutRef.current);
      inactivityTimeoutRef.current = setTimeout(() => {
        setIsUserInteracting(false);
      }, 3000);
    };

    updateTargetPosition(true);

    const allInputs: Element[] = [];
    fieldRefs.forEach((fieldRef) => {
      const element = document.getElementById(fieldRef);
      if (element) {
        const inputs = element.querySelectorAll('input, textarea, select');
        inputs.forEach(input => {
          allInputs.push(input);
          input.addEventListener('focus', handleFocus);
          input.addEventListener('blur', handleBlur);
          input.addEventListener('input', handleInput);
          input.addEventListener('change', handleInput);
        });
      }
    });

    const handleResize = () => updateTargetPosition(false);
    const handleScroll = () => updateTargetPosition(false);

    window.addEventListener('resize', handleResize);
    if (placement !== 'top') {
      window.addEventListener('scroll', handleScroll, true);
    }

    const resizeObserver = new ResizeObserver(() => updateTargetPosition(false));
    const observedIds = new Set<string>();
    const observeFieldRefs = () => {
      fieldRefs.forEach((fieldRef) => {
        if (!observedIds.has(fieldRef)) {
          const element = document.getElementById(fieldRef);
          if (element) {
            resizeObserver.observe(element);
            observedIds.add(fieldRef);
          }
        }
      });
    };
    observeFieldRefs();

    const mutationObserver = new MutationObserver(() => {
      observeFieldRefs();
      updateTargetPosition(false);
    });
    mutationObserver.observe(document.body, { childList: true, subtree: true });

    return () => {
      window.removeEventListener('resize', handleResize);
      if (placement !== 'top') {
        window.removeEventListener('scroll', handleScroll, true);
      }
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      if (inactivityTimeoutRef.current) clearTimeout(inactivityTimeoutRef.current);
      allInputs.forEach(input => {
        input.removeEventListener('focus', handleFocus);
        input.removeEventListener('blur', handleBlur);
        input.removeEventListener('input', handleInput);
        input.removeEventListener('change', handleInput);
      });
    };
  }, [fieldRefs, placement]);

  useEffect(() => {
    hasScrolledRef.current = false;
  }, [fieldRefs]);

  const autoProgressionDelay = useMemo(() => {
    return currentStep.fieldType === 'text' ? 1000 : 2000;
  }, [currentStep.fieldType]);

  useEffect(() => {
    if (ENABLE_AUTO_PROGRESSION && isFieldValid && !isUserInteracting) {
      timeoutRef.current = setTimeout(() => {
        onNext();
      }, autoProgressionDelay);
    }
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [isFieldValid, isUserInteracting, onNext, autoProgressionDelay]);

  const boundingBox = useMemo(() => {
    if (targetRects.length === 0) return null;
    const left = Math.min(...targetRects.map(r => r.left));
    const top = Math.min(...targetRects.map(r => r.top));
    const right = Math.max(...targetRects.map(r => r.right));
    const bottom = Math.max(...targetRects.map(r => r.bottom));
    return { left, top, right, bottom, width: right - left, height: bottom - top };
  }, [targetRects]);

  const tooltipStyle = useMemo(() => {
    if (!boundingBox) return {};

    const targetRect = boundingBox;
    const isMobileView = window.innerWidth < 768;
    const spacing = 16;
    const margin = 16;

    // Mobile : caché
    if (isMobileView) {
      return { display: 'none' as const };
    }

    // Desktop : toujours à gauche de l'élément ciblé
    const availableLeft = targetRect.left - spacing;
    const tooltipWidth = Math.min(400, Math.max(280, availableLeft - margin));

    let left = targetRect.left - tooltipWidth - spacing;
    let top: number;

    // Calculer la partie VISIBLE de l'élément ciblé dans le viewport
    const visibleTop = Math.max(0, targetRect.top);
    const visibleBottom = Math.min(window.innerHeight, targetRect.bottom);
    const visibleHeight = visibleBottom - visibleTop;

    if (placement === 'top') {
      top = visibleTop;
    } else if (visibleHeight > 0 && targetRect.height > window.innerHeight * 0.5) {
      // Élément très grand : centrer sur la partie VISIBLE
      const visibleCenter = visibleTop + visibleHeight / 2;
      top = visibleCenter - tooltipHeight / 2;
    } else {
      // Élément normal : centrer sur l'élément entier
      top = targetRect.top + targetRect.height / 2 - tooltipHeight / 2;
    }

    // Clamp vertical avec la hauteur RÉELLE du tooltip
    if (top < margin) top = margin;
    if (top + tooltipHeight > window.innerHeight - margin) {
      top = window.innerHeight - tooltipHeight - margin;
    }

    // Fallback en dessous si pas de place à gauche (< 280px)
    if (availableLeft < 280 + margin) {
      left = Math.max(margin, targetRect.left);
      top = targetRect.bottom + spacing;
      const fallbackWidth = Math.min(400, window.innerWidth - left - margin);

      if (top + tooltipHeight > window.innerHeight - margin) {
        top = window.innerHeight - tooltipHeight - margin;
      }

      return {
        position: 'fixed' as const,
        left: `${left}px`,
        top: `${top}px`,
        maxWidth: `${fallbackWidth}px`,
        zIndex: 60,
      };
    }

    return {
      position: 'fixed' as const,
      left: `${left}px`,
      top: `${top}px`,
      maxWidth: `${tooltipWidth}px`,
      zIndex: 60,
    };
  }, [boundingBox, placement, tooltipHeight]);

  if (!boundingBox) return null;

  return (
    <>
      {/* Bordure autour de tous les champs ciblés */}
      <div
        className="tw-fixed tw-border-2 tw-border-affinicar-primary tw-rounded-lg tw-pointer-events-none tw-transition-all tw-duration-200"
        style={{
          left: `${boundingBox.left - 8}px`,
          top: `${boundingBox.top - 8}px`,
          width: `${boundingBox.width + 16}px`,
          height: `${boundingBox.height + 16}px`,
          zIndex: 59,
          boxShadow: '0 0 0 4px rgba(128, 116, 175, 0.15), 0 4px 12px rgba(128, 116, 175, 0.1)',
        }}
      />

      {/* Tooltip flottant */}
      <div
        ref={tooltipRef}
        className="tw-bg-white tw-rounded-lg tw-shadow-2xl tw-border tw-border-affinicar-light tw-pointer-events-auto tw-max-h-[70vh] tw-overflow-y-auto"
        style={tooltipStyle}
      >
        {/* Header */}
        <div className="tw-bg-gradient-to-r tw-from-affinicar-primary tw-to-affinicar-secondary tw-px-4 tw-py-3 tw-rounded-t-lg">
          <div className="tw-flex tw-items-center tw-justify-between tw-mb-1">
            <div className="tw-flex tw-items-center tw-gap-2">
              <Lightbulb className="tw-w-5 tw-h-5 tw-text-white" />
              <span className="tw-text-sm tw-font-medium tw-text-white">
                Étape {stepNumber} sur {totalSteps}
              </span>
            </div>
            <button
              onClick={onSkip}
              className="tw-text-white hover:tw-text-affinicar-lighter tw-transition-colors"
              title="Passer le tutoriel"
            >
              <X className="tw-w-5 tw-h-5" />
            </button>
          </div>
          <h3 className="tw-text-lg tw-font-bold tw-text-white">{currentStep.title}</h3>
        </div>

        {/* Content */}
        <div className="tw-p-4">
          <p className="tw-text-sm tw-text-gray-700 tw-mb-4">{currentStep.content}</p>

          {/* Progress bar */}
          <div className="tw-mb-4">
            <div className="tw-w-full tw-bg-gray-200 tw-rounded-full tw-h-2">
              <div
                className="tw-bg-affinicar-primary tw-h-2 tw-rounded-full tw-transition-all tw-duration-300"
                style={{ width: `${(stepNumber / totalSteps) * 100}%` }}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="tw-flex tw-items-center tw-justify-between tw-gap-3">
            <div>
              {showNavigation && stepNumber > 1 && (
                <button
                  onClick={onPrevious}
                  className="tw-flex tw-items-center tw-gap-1 tw-px-3 tw-py-2 tw-text-sm tw-text-gray-600 hover:tw-text-gray-800 tw-border tw-border-gray-300 tw-rounded-lg hover:tw-bg-gray-50 tw-transition-colors"
                >
                  <ChevronRight className="tw-w-4 tw-h-4 tw-rotate-180" />
                  Précédent
                </button>
              )}
            </div>

            <div className="tw-flex tw-items-center tw-gap-2">
              {showNavigation && onSaveDraft && (
                <button
                  onClick={onSaveDraft}
                  disabled={isSavingDraft}
                  className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-text-sm tw-text-affinicar-primary tw-border tw-border-affinicar-light tw-rounded-lg hover:tw-bg-affinicar-lighter tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                >
                  <Save className="tw-w-3.5 tw-h-3.5" />
                  {isSavingDraft ? 'Sauvegarde...' : saveLabel}
                </button>
              )}
              {showNavigation && (ENABLE_AUTO_PROGRESSION && isFieldValid && !isUserInteracting ? (
                <div className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-green-600">
                  <svg className="tw-w-5 tw-h-5 tw-animate-spin" viewBox="0 0 24 24">
                    <circle
                      className="tw-opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="tw-opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Passage automatique...
                </div>
              ) : (
                <button
                  onClick={onNext}
                  disabled={!isFieldValid}
                  className="tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-bg-affinicar-primary tw-text-white tw-rounded-lg hover:tw-bg-affinicar-secondary disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-transition-colors tw-text-sm tw-font-medium"
                >
                  Suivant
                  <ChevronRight className="tw-w-4 tw-h-4" />
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
